import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../utils/supabase';
import { addPlayersToSession, removePlayersFromSession, getSessionPlayers, updateSessionPlayerStatus } from '../services/sessionService';
import {
  canUseOfflineRosterFallback,
  OFFLINE_COMMAND_TYPES,
} from '../utils/session/offlineQueueConstants';

/**
 * Custom hook for managing player data and operations for a badminton club
 * 
 * This hook provides a clean separation of concerns with structured data objects,
 * proper error handling, optimistic updates, and no UI side effects.
 * 
 * @param {string|null} clubId - The ID of the club to manage players for
 * @returns {Object} Hook state and functions
 * 
 * @example
 * const MyComponent = () => {
 *   const {
 *     players,
 *     activePlayers,
 *     loading,
 *     error,
 *     lastOperation,
 *     sessionStats,
 *     togglePlayerStatus,
 *     bulkToggleStatus,
 *     clearError
 *   } = usePlayerManagement('club-123');
 * 
 *   const handleTogglePlayer = async (playerId) => {
 *     const result = await togglePlayerStatus(playerId, courtAssignments);
 *     if (!result.success) {
 *       // Handle error - show toast notification, etc.
 *       console.error(result.error);
 *     }
 *   };
 * 
 *   const handleBulkActivate = async () => {
 *     const result = await bulkToggleStatus('activate', courtAssignments);
 *     if (result.success) {
 *       console.log(`Activated ${result.updatedCount} players`);
 *     }
 *   };
 * 
 *   return (
 *     <div>
 *       {loading && <div>Loading...</div>}
 *       {error && <div>Error: {error}</div>}
 *       {lastOperation?.status === 'success' && (
 *         <div>Operation completed successfully!</div>
 *       )}
 *       <div>Active players: {sessionStats.activePlayers}</div>
 *     </div>
 *   );
 * };
 */
const usePlayerManagement = (clubId = null, sessionId = null, seasonId = null, options = {}) => {
  const offlineRosterBridgeRef = options.offlineRosterBridgeRef;
  // State management
  const [playersById, setPlayersById] = useState({});
  const lastGoodPlayersById = useRef({});
  const [sessionPlayerStatus, setSessionPlayerStatus] = useState({}); // { playerId: status }
  const [sessionPlayerPenalties, setSessionPlayerPenalties] = useState({}); // { playerId: late_joiner_penalty }
  const [sessionPlayerExitReasons, setSessionPlayerExitReasons] = useState({}); // { playerId: exitReason }
  const [sessionPlayerScheduledGames, setSessionPlayerScheduledGames] = useState({}); // { playerId: scheduled_game_id }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastOperation, setLastOperation] = useState(null);

  /**
   * Validates that a club ID is provided
   * @param {string} id - Club ID to validate
   * @throws {Error} If club ID is missing or invalid
   */
  const validateClubId = useCallback((id) => {
    if (!id || typeof id !== 'string') {
      throw new Error('Valid club ID is required');
    }
  }, []);
  
  /**
   * Formats player data with structured separation of player info and club stats
   * @param {Object} row - Raw data row from Supabase
   * @returns {Object} Formatted player object with playerInfo and clubStats
   */
  const formatPlayerData = useCallback((row, clubData = {}, eloData = {}, playersEloDataKeys = [], sessionGamesData = {}, sessionPlayerData = {}) => {
    const gamesPlayedActual = sessionPlayerData.games_played ?? sessionGamesData.count ?? 0;
    const effectiveGamesPlayed = sessionPlayerData.effective_games_played ?? gamesPlayedActual;
    const gamesPlayedDisplay = effectiveGamesPlayed !== gamesPlayedActual
      ? `${gamesPlayedActual} (eff: ${effectiveGamesPlayed})`
      : `${gamesPlayedActual}`;
    
    const playerInfo = {
      id: row.player?.id,
      first_name: row.player?.first_name,
      last_name: row.player?.last_name,
      is_guest: row.player?.is_guest ?? false,
      email: row.player?.email,
      gender: row.player?.gender,
      guest_invited_email: row.player?.guest_invited_email ?? null,
      merged_at: row.player?.merged_at ?? null,
      is_active: row.player?.is_active ?? true,
      name: [row.player?.first_name, row.player?.last_name].filter(Boolean).join(' '),
      avatar_url: row.player?.avatar_url,
    };
    return {
      ...playerInfo,
      levels_elo_rating: eloData.levels_elo_rating ?? (() => {
        const errorMsg = `🚨 ELO DEBUG: levels_elo_rating is missing for player ${playerInfo.name} (ID: ${playerInfo.id})`;
        console.error(errorMsg);
        console.error('🚨 ELO DEBUG: eloData:', eloData);
        console.error('🚨 ELO DEBUG: player_id from row:', row.player_id);
        console.error('🚨 ELO DEBUG: playersEloData keys:', playersEloDataKeys);
        // Store error in localStorage so it persists through reloads
        localStorage.setItem('elo_debug_error', JSON.stringify({
          error: errorMsg,
          playerInfo,
          eloData,
          player_id: row.player_id,
          playersEloDataKeys,
          timestamp: new Date().toISOString()
        }));
        return 1200; // Return 1200 instead of throwing to prevent reload
      })(),
      mixed_elo_rating: eloData.mixed_elo_rating ?? (() => {
        const errorMsg = `🚨 ELO DEBUG: mixed_elo_rating is missing for player ${playerInfo.name} (ID: ${playerInfo.id})`;
        console.error(errorMsg);
        console.error('🚨 ELO DEBUG: eloData:', eloData);
        console.error('🚨 ELO DEBUG: player_id from row:', row.player_id);
        console.error('🚨 ELO DEBUG: playersEloData keys:', playersEloDataKeys);
        // Store error in localStorage so it persists through reloads
        localStorage.setItem('elo_debug_error', JSON.stringify({
          error: errorMsg,
          playerInfo,
          eloData,
          player_id: row.player_id,
          playersEloDataKeys,
          timestamp: new Date().toISOString()
        }));
        return 1200; // Return 1200 instead of throwing to prevent reload
      })(),
      // From player_club_elo_ratings.singles_elo_rating — null when column is NULL or row missing (never infer from doubles)
      singles_elo_rating: eloData.singles_elo_rating != null ? eloData.singles_elo_rating : null,
      club_games_played: clubData.total_games_played ?? 0,
      mixed_games_played: clubData.mixed_games_played ?? 0,
      levels_games_played: clubData.levels_games_played ?? 0,
      gamesPlayedThisSession: sessionGamesData.count ?? 0,
      sessionMixedGames: sessionGamesData.mixed ?? 0,
      sessionLevelsGames: sessionGamesData.levels ?? 0,
      gamesPlayedActual,
      effectiveGamesPlayed,
      activatedAt: sessionPlayerData.activated_at,
      gamesPlayedDisplay,
    };
  }, []);
    
  /**
   * Fetches all players for a club with their stats
   * @param {string} [clubIdParam] - Club ID (defaults to hook clubId)
   * @param {string} [seasonIdParam] - Season ID (defaults to hook seasonId)
   * @param {Object} [fetchOptions] - Pass `{ silent: true }` for background refresh (no global loading / overlay)
   * @returns {Promise<Object>} Result object with success status and data/error
   */
  const fetchPlayers = useCallback(async (clubIdParam = clubId, seasonIdParam = seasonId, fetchOptions = {}) => {
    const silent = fetchOptions && typeof fetchOptions === 'object' && fetchOptions.silent === true;

    // Check for any stored ELO debug errors from previous loads
    const storedError = localStorage.getItem('elo_debug_error');
    if (storedError) {
      try {
        const errorData = JSON.parse(storedError);
        console.error('🚨 ELO DEBUG: Previous error found:', errorData);
        console.error('🚨 ELO DEBUG: Error occurred at:', errorData.timestamp);
        // Clear the stored error after logging
        localStorage.removeItem('elo_debug_error');
      } catch (e) {
        console.error('Failed to parse stored ELO debug error:', e);
      }
    }

    if (canUseOfflineRosterFallback(offlineRosterBridgeRef?.current)) {
      if (!silent) setLoading(false);
      return { success: true, data: lastGoodPlayersById.current, skipped: true };
    }

    if (!silent) {
      setLoading(true);
      setError(null);
      setLastOperation({ type: 'fetch', status: 'loading' });
    }

    // Defensive check
    if (!clubIdParam) {
      console.warn('[fetchPlayers] Skipping query: missing clubId', { clubId: clubIdParam, seasonId: seasonIdParam });
      if (!silent) setLoading(false);
      return { success: false, error: 'Missing clubId' };
    }
    
    // Season handling is now done in the query logic below

    try {
      validateClubId(clubIdParam);
      console.log('[fetchPlayers] Querying player_club_elo_ratings with:', { clubId: clubIdParam, seasonId: seasonIdParam });

      let sessionRosterIds = null;
      if (sessionId) {
        const { data: spRows, error: spRosterErr } = await supabase
          .from('session_players')
          .select('player_id')
          .eq('session_id', sessionId);
        if (!spRosterErr && spRows?.length) {
          sessionRosterIds = new Set(spRows.map((r) => r.player_id));
        }
      }

      // First, get current season for this club
      const { data: currentSeason, error: seasonError } = await supabase
        .from('seasons')
        .select('id')
        .eq('club_id', clubIdParam)
        .eq('season_status', 'current')
        .maybeSingle();
        
      if (seasonError) {
        console.warn('[fetchPlayers] Could not fetch current season:', seasonError);
      } else if (currentSeason) {
        console.log('[fetchPlayers] Found current season for main query:', currentSeason.id);
      } else {
        console.log('[fetchPlayers] No current season found, will fetch all ELO data');
      }

      // Go back to separate queries - first get players from players_club
      let query = supabase
        .from('players_club')
        .select(`
          player:players(
            id,
            first_name,
            last_name,
            email,
            gender,
            is_guest,
            is_active,
            guest_created_by,
            merged_at,
            guest_merge_declined_at,
            guest_invited_email,
            avatar_url
          ),
          club_id,
          player_id,
          is_active,
          club_role
        `)
        .eq('club_id', clubIdParam);
        
      console.log('[fetchPlayers] Main query SQL equivalent:', `SELECT player:players(*), club_id, player_id, is_active, club_role, elo_ratings:player_club_elo_ratings!inner(mixed_elo_rating, levels_elo_rating, season_id) FROM players_club WHERE club_id = '${clubIdParam}'${currentSeason ? ` AND elo_ratings.season_id = '${currentSeason.id}'` : ''}`);
      
      // No season filtering needed for players_club query
      console.log('[fetchPlayers] Fetching all players for club from players_club table');
      
      const { data, error: supabaseError } = await query;

      const rows = Array.isArray(data) ? data : [];

      if (
        silent &&
        sessionId &&
        rows.length === 0 &&
        Object.keys(lastGoodPlayersById.current).length > 0
      ) {
        console.warn(
          '[fetchPlayers] Silent fetch returned 0 players_club rows; preserving existing playersById to avoid blanking session UI'
        );
        return { success: true, data: { ...lastGoodPlayersById.current } };
      }

      console.log('[fetchPlayers] Raw database response:', rows);
      console.log('[fetchPlayers] Database error:', supabaseError);
      console.log('[fetchPlayers] Number of rows returned:', rows.length || 0);
      
      // Debug: Check if there are any rows in the table at all
      if (!rows || rows.length === 0) {
        console.log('[fetchPlayers] No ELO data found, checking if table has any rows...');
        const { data: countData, error: countError } = await supabase
          .from('player_club_elo_ratings')
          .select('id, player_id, club_id, season_id, mixed_elo_rating, levels_elo_rating, singles_elo_rating')
          .limit(5);
        console.log('[fetchPlayers] Sample rows from player_club_elo_ratings:', countData);
        console.log('[fetchPlayers] Count query error:', countError);
      }
      
      if (rows && rows.length > 0) {
        console.log('[fetchPlayers] First player ELO data:', {
          player_id: rows[0].player_id,
          mixed_elo_rating: rows[0].mixed_elo_rating,
          levels_elo_rating: rows[0].levels_elo_rating,
          player_name: rows[0].player?.name
        });
      }

      // Separately fetch players_club data to get total_games_played from the view
      let playersClubData = {};
      if (rows && rows.length > 0) {
        const playerIds = rows.map(row => row.player_id);
        const { data: clubData, error: clubError } = await supabase
          .from('player_club_total_games')
          .select('player_id, total_games_played, mixed_games_played, levels_games_played')
          .eq('club_id', clubIdParam)
          .in('player_id', playerIds);
          
        if (clubError) {
          console.warn('[fetchPlayers] Could not fetch club games data:', clubError);
        } else {
          clubData.forEach(row => {
            playersClubData[row.player_id] = row;
          });
        }
      }

      // Separately fetch current season ELO data (same pattern as games played)
      let playersEloData = {};
      if (rows && rows.length > 0) {
        const playerIds = rows.map(row => row.player_id);
        console.log('[fetchPlayers] Fetching ELO data for', playerIds.length, 'players');
        
        if (currentSeason) {
          console.log('[fetchPlayers] Using current season for ELO:', currentSeason.id);
          
          // Fetch ELO data for current season
          const { data: eloData, error: eloError } = await supabase
            .from('player_club_elo_ratings')
            .select('player_id, mixed_elo_rating, levels_elo_rating, singles_elo_rating')
            .eq('club_id', clubIdParam)
            .eq('season_id', currentSeason.id)
            .in('player_id', playerIds);
            
          console.log('[fetchPlayers] ELO query result:', { eloData, eloError, count: eloData?.length || 0 });
            
          if (eloError) {
            console.warn('[fetchPlayers] Could not fetch ELO data:', eloError);
          } else {
            eloData.forEach(row => {
              playersEloData[row.player_id] = row;
            });
            console.log('[fetchPlayers] ELO data fetched for', eloData.length, 'players');
            console.log('[fetchPlayers] ELO data sample:', eloData.slice(0, 2));
          }
        } else {
          console.log('[fetchPlayers] No current season found, ELO will use defaults');
        }
      }

      // Separately fetch session games played data
      let playersSessionGamesData = {};
      if (rows && rows.length > 0 && sessionId) {
        const playerIds = rows.map(row => row.player_id);
        console.log('[fetchPlayers] Fetching session games played for', playerIds.length, 'players in session', sessionId);

        const { data: sessionGamesData, error: sessionGamesError } = await supabase
          .from('player_game_history')
          .select('player_id, game_type')
          .eq('session_id', sessionId)
          .eq('club_id', clubIdParam)
          .in('player_id', playerIds);

        if (sessionGamesError) {
          console.warn('[fetchPlayers] Could not fetch session games played data:', sessionGamesError);
        } else {
          // Count games per player and by game type
          sessionGamesData.forEach(row => {
            const playerId = row.player_id;
            if (!playersSessionGamesData[playerId]) {
              playersSessionGamesData[playerId] = { count: 0, mixed: 0, levels: 0 };
            }
            playersSessionGamesData[playerId].count += 1;
            
            // Count by game type
            if (row.game_type === 'mixed') {
              playersSessionGamesData[playerId].mixed += 1;
            } else if (row.game_type === 'levels') {
              playersSessionGamesData[playerId].levels += 1;
            }
          });
          console.log('[fetchPlayers] Session games data fetched for', Object.keys(playersSessionGamesData).length, 'players');
          console.log('[fetchPlayers] Session games data sample:', Object.entries(playersSessionGamesData).slice(0, 2));
        }
      }

      // Fetch session player metrics (games_played, effective_games_played, activated_at)
      let sessionPlayerMetrics = {};
      if (rows && rows.length > 0 && sessionId) {
        const playerIds = rows.map(row => row.player_id);
        console.log('[fetchPlayers] Fetching session player metrics for', playerIds.length, 'players in session', sessionId);

        const { data: sessionPlayersData, error: sessionPlayersError } = await supabase
          .from('session_players')
          .select('player_id, games_played, effective_games_played, activated_at')
          .eq('session_id', sessionId)
          .in('player_id', playerIds);

        if (sessionPlayersError) {
          console.warn('[fetchPlayers] Could not fetch session player metrics:', sessionPlayersError);
        } else {
          sessionPlayersData.forEach(row => {
            sessionPlayerMetrics[row.player_id] = {
              games_played: row.games_played,
              effective_games_played: row.effective_games_played,
              activated_at: row.activated_at
            };
          });
          console.log('[fetchPlayers] Session player metrics fetched for', Object.keys(sessionPlayerMetrics).length, 'players');
          console.log('[fetchPlayers] Session player metrics sample:', Object.entries(sessionPlayerMetrics).slice(0, 2));
        }
      }

      if (supabaseError) throw supabaseError;

      const playerMap = {};
      rows.forEach(row => {
        // Exclude archived guests and merged guests from session player list (Player Management, waitlist, etc.)
        // so hosts don't see duplicate entries (e.g. main account + archived/merged guest with same name)
        // Archived = guest with inactive membership (players_club.is_active) OR inactive player record (players.is_active)
        const isArchivedGuest =
          row.player?.is_guest === true &&
          (row.is_active === false || row.player?.is_active === false);
        const isMergedGuest = row.player?.is_guest === true && row.player?.merged_at != null;
        if (isArchivedGuest || isMergedGuest) return;

        const clubData = playersClubData[row.player_id] || {};
        const eloData = playersEloData[row.player_id] || {};
        const sessionGamesData = playersSessionGamesData[row.player_id] || {};
        const sessionPlayerData = sessionPlayerMetrics[row.player_id] || {};
        
        // Debug logging removed for cleaner console
        
        const player = formatPlayerData(row, clubData, eloData, Object.keys(playersEloData), sessionGamesData, sessionPlayerData);
        
        // Debug logging removed for cleaner console
        
        playerMap[player.id] = player;
        
        // Debug first player's ELO values
        if (Object.keys(playerMap).length === 1) {
          console.log('[fetchPlayers] First formatted player ELO:', {
            player_id: player.id,
            player_name: player.name,
            mixed_elo_rating: player.mixed_elo_rating,
            levels_elo_rating: player.levels_elo_rating,
            singles_elo_rating: player.singles_elo_rating,
            gamesPlayedThisSession: player.gamesPlayedThisSession
          });
        }
      });
      setPlayersById(prev => {
        const updated = { ...prev };
        let changed = false;
        for (const id in playerMap) {
          if (!prev[id] || !shallowEqual(prev[id], playerMap[id])) {
            updated[id] = playerMap[id];
            changed = true;
          }
        }
        for (const id in prev) {
          if (!(id in playerMap)) {
            if (sessionRosterIds && sessionRosterIds.has(id)) {
              // Keep roster entries that were missing from this fetch (transient RLS/network/partial
              // responses) so session courts/upcoming queues do not blank player slots.
              continue;
            }
            delete updated[id];
            changed = true;
          }
        }
        if (changed) {
          lastGoodPlayersById.current = updated;
        }
        return changed ? updated : prev;
      });
      if (!silent) {
        setLastOperation({ type: 'fetch', status: 'success', count: Object.keys(playerMap).length });
      }
      return { success: true, data: playerMap };
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch players';
      setError(errorMessage);
      if (!silent) {
        setLastOperation({ type: 'fetch', status: 'error', error: errorMessage });
      }
      return { success: false, error: errorMessage };
    } finally {
      if (!silent) setLoading(false);
    }
  }, [clubId, seasonId, sessionId, validateClubId, formatPlayerData, offlineRosterBridgeRef]);

  /**
   * Loads session_players rows for the session into sessionPlayerStatus maps (all statuses).
   * @param {Object} [options]
   * @param {boolean} [options.silent] - If true, do not toggle global loading (e.g. Realtime refresh)
   */
  const fetchSessionPlayerStatus = useCallback(async (options = {}) => {
    const silent = options.silent === true;
    if (!sessionId) return;
    if (canUseOfflineRosterFallback(offlineRosterBridgeRef?.current)) {
      return;
    }
    if (!silent) setLoading(true);
    try {
      const sessionPlayers = await getSessionPlayers(sessionId);
      const statusMap = {};
      const exitReasonMap = {};
      const penaltyMap = {};
      sessionPlayers.forEach(sp => { 
        statusMap[sp.player_id] = sp.status;
        exitReasonMap[sp.player_id] = sp.exit_reason;
        penaltyMap[sp.player_id] = sp.late_joiner_penalty || 0;
      });
      setSessionPlayerStatus(statusMap);
      setSessionPlayerExitReasons(exitReasonMap);
      setSessionPlayerPenalties(penaltyMap);
    } catch (err) {
      setError(err.message || 'Failed to fetch session players');
      if (!silent) {
        setSessionPlayerStatus({});
        setSessionPlayerExitReasons({});
        setSessionPlayerPenalties({});
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [sessionId, offlineRosterBridgeRef]);

  /** Merge one session_players row into local roster status maps (realtime passive tabs). */
  const patchSessionPlayerFromRow = useCallback((row) => {
    if (!row?.player_id) return;
    const playerId = row.player_id;
    if (row.status != null) {
      setSessionPlayerStatus((prev) => {
        if (prev[playerId] === row.status) return prev;
        return { ...prev, [playerId]: row.status };
      });
    }
    if (row.exit_reason !== undefined) {
      setSessionPlayerExitReasons((prev) => {
        const nextReason = row.exit_reason ?? null;
        if (prev[playerId] === nextReason) return prev;
        return { ...prev, [playerId]: nextReason };
      });
    }
    if (row.late_joiner_penalty != null) {
      setSessionPlayerPenalties((prev) => {
        if (prev[playerId] === row.late_joiner_penalty) return prev;
        return { ...prev, [playerId]: row.late_joiner_penalty };
      });
    }
  }, []);

  // Note: calculateAdjustedGamesPlayed and calculateMode functions removed
  // The new late joiner penalty system is handled by updateSessionPlayerStatus in sessionService.js

  /**
   * Updates a player's status in the session with proper state transitions
   * @param {string} playerId - Player ID to update
   * @param {string} targetStatus - Target status ('signed_up', 'active', 'paused', 'inactive')
   * @param {string} exitReason - Exit reason if becoming inactive ('finished', 'no_show', 'cancelled')
   * @returns {Promise<Object>} Result object with success status and data/error
   */
  const togglePlayerStatus = useCallback(async (playerId, targetStatus = null, exitReason = null) => {
    console.log('togglePlayerStatus called with:', { playerId, targetStatus, exitReason });
    setLoading(true);
    setError(null);
    setLastOperation({ type: 'toggle', status: 'loading', playerId });

    try {
      if (!sessionId) throw new Error('No session ID');

      const offlineBridge = offlineRosterBridgeRef?.current;
      const skipNetworkRead = canUseOfflineRosterFallback(offlineBridge);

      // First, check if player exists in session_players table to get accurate status.
      // maybeSingle: 0 rows => null (no row); .single() causes HTTP 406 + PGRST116 noise when absent.
      let sessionPlayerData = null;
      if (!skipNetworkRead) {
        const { data, error: sessionPlayerError } = await supabase
          .from('session_players')
          .select('status, exit_reason, games_played, effective_games_played, activated_at')
          .eq('session_id', sessionId)
          .eq('player_id', playerId)
          .maybeSingle();
        if (sessionPlayerError) {
          if (!canUseOfflineRosterFallback(offlineBridge, sessionPlayerError)) {
            console.error('Error fetching session player data:', sessionPlayerError);
            throw sessionPlayerError;
          }
        } else {
          sessionPlayerData = data;
        }
      }
      
      // Use database status if available, otherwise fall back to local state
      const currentStatus = sessionPlayerData?.status || sessionPlayerStatus[playerId];
      let newStatus = targetStatus;
      let newExitReason = exitReason;
      
      console.log('togglePlayerStatus - initial values:', { 
        newStatus, 
        newExitReason, 
        currentStatus,
        databaseStatus: sessionPlayerData?.status,
        localStatus: sessionPlayerStatus[playerId]
      });

      // If no target status specified, determine based on current status
      if (!targetStatus) {
        if (currentStatus === 'signed_up') {
          newStatus = 'active'; // Player arrives
        } else if (currentStatus === 'active') {
          newStatus = 'paused'; // Player takes a break
        } else if (currentStatus === 'paused') {
          newStatus = 'active'; // Player resumes
        } else if (currentStatus === 'inactive') {
          newStatus = 'active'; // Player returns
        } else {
          newStatus = 'signed_up'; // Add new player
        }
      }

      // Set exit reason based on the transition
      if (newStatus === 'inactive' && !newExitReason) {
        // Determine exit reason based on current status
        if (currentStatus === 'signed_up') {
          newExitReason = 'no_show'; // Player never arrived
        } else {
          newExitReason = 'finished'; // Player was active/paused and finished
        }
      }

      // Prevent invalid status transitions: cannot mark as cancelled or no_show if player is currently active
      if (currentStatus === 'active' && (newStatus === 'cancelled' || (newStatus === 'inactive' && newExitReason === 'no_show'))) {
        const errorMessage = 'Cannot mark a player as cancelled or no show while they are actively playing. Please pause or finish them first.';
        setError(errorMessage);
        setLastOperation({ type: 'toggle', status: 'error', playerId, error: errorMessage });
        return { success: false, error: errorMessage };
      }

      // Handle the different scenarios
      if (!currentStatus && !sessionPlayerData) {
        // Player not in session - add them
        console.log('Player not in session - adding new player');
        try {
          await addPlayersToSession(sessionId, [playerId], clubId, newStatus);
        } catch (addError) {
          if (canUseOfflineRosterFallback(offlineBridge, addError)) {
            await offlineBridge.enqueue({
              type: OFFLINE_COMMAND_TYPES.ADD_PLAYER,
              playerId,
              status: newStatus,
            });
          } else {
            throw addError;
          }
        }
        setSessionPlayerStatus(status => ({ ...status, [playerId]: newStatus }));
        setSessionPlayerExitReasons(reasons => ({ ...reasons, [playerId]: null }));
      } else if (newStatus === 'remove') {
        const gamesPlayed = sessionPlayerData?.games_played ?? playersById[playerId]?.gamesPlayedThisSession ?? 0;
        const effectiveGames = sessionPlayerData?.effective_games_played ?? gamesPlayed;
        if (gamesPlayed > 0 || effectiveGames > 0) {
          const errorMessage = 'This player has already played — use Finished instead.';
          setError(errorMessage);
          setLastOperation({ type: 'toggle', status: 'error', playerId, error: errorMessage });
          return { success: false, error: errorMessage };
        }

        const { error: signupDeleteError } = await supabase
          .from('session_signups')
          .delete()
          .eq('session_id', sessionId)
          .eq('player_id', playerId);

        if (signupDeleteError) {
          console.error('Error deleting session_signups on remove:', signupDeleteError);
        }

        try {
          await removePlayersFromSession(sessionId, [playerId]);
        } catch (removeError) {
          if (canUseOfflineRosterFallback(offlineBridge, removeError)) {
            await offlineBridge.enqueue({
              type: OFFLINE_COMMAND_TYPES.REMOVE_PLAYER,
              playerId,
            });
          } else {
            throw removeError;
          }
        }
        setSessionPlayerStatus(status => {
          const copy = { ...status };
          delete copy[playerId];
          return copy;
        });
        setSessionPlayerExitReasons(reasons => {
          const copy = { ...reasons };
          delete copy[playerId];
          return copy;
        });
        setSessionPlayerPenalties(penalties => {
          const copy = { ...penalties };
          delete copy[playerId];
          return copy;
        });
        newStatus = undefined;
      } else {
        // Player already in session - update their status
        console.log('togglePlayerStatus - updating player:', { playerId, newStatus, newExitReason, currentStatus });
        
        // Use updateSessionPlayerStatus function which handles:
        // - activated_at timestamp setting
        // - exit_reason handling
        // Penalty handling moved to Dashboard via modal
        console.log('Using updateSessionPlayerStatus for status change:', { currentStatus, newStatus });
        try {
          await updateSessionPlayerStatus(sessionId, playerId, newStatus, newExitReason);
        } catch (statusError) {
          if (canUseOfflineRosterFallback(offlineBridge, statusError)) {
            await offlineBridge.enqueue({
              type: OFFLINE_COMMAND_TYPES.PLAYER_STATUS,
              playerId,
              status: newStatus,
              exitReason: newExitReason,
            });
          } else {
            throw statusError;
          }
        }
        
        // If marking as cancelled, also update session_signups to keep them in sync
        if (newStatus === 'cancelled') {
          console.log('togglePlayerStatus - also updating session_signups to cancelled');
          const { error: signupError } = await supabase
            .from('session_signups')
            .update({ signup_status: 'cancelled' })
            .eq('session_id', sessionId)
            .eq('player_id', playerId);
          
          if (signupError) {
            console.error('Error updating session_signups:', signupError);
            // Don't throw - session_players update succeeded
          }
        }
        
        console.log('togglePlayerStatus - updating state with:', { newStatus, newExitReason });
        setSessionPlayerStatus(status => {
          const newStatusState = { ...status, [playerId]: newStatus };
          console.log('togglePlayerStatus - new status state:', newStatusState);
          return newStatusState;
        });
        setSessionPlayerExitReasons(reasons => {
          const newExitReasonsState = { ...reasons, [playerId]: newExitReason };
          console.log('togglePlayerStatus - new exit reasons state:', newExitReasonsState);
          return newExitReasonsState;
        });
        
        console.log('togglePlayerStatus - update completed');
      }

      setLastOperation({ type: 'toggle', status: 'success', playerId, newStatus, exitReason: newExitReason });
      return { success: true, newStatus, exitReason: newExitReason };
    } catch (err) {
      const errorMessage = err.message || 'Failed to update player session status';
      setError(errorMessage);
      setLastOperation({ type: 'toggle', status: 'error', playerId, error: errorMessage });
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [sessionId, clubId, sessionPlayerStatus]);

  /**
   * Updates a player's status in the session with exit reason
   * @param {string} playerId - Player ID to update
   * @param {string} status - New status ('signed_up', 'active', 'paused', 'inactive')
   * @param {string} exitReason - Exit reason if becoming inactive
   * @returns {Promise<Object>} Result object with success status and data/error
   */
  const setPlayerSessionStatus = useCallback(async (playerId, status, exitReason = null) => {
    setLoading(true);
    setError(null);
    try {
      if (!sessionId) throw new Error('No session ID');
      await updateSessionPlayerStatus(sessionId, playerId, status, exitReason);
      setSessionPlayerStatus(prev => ({ ...prev, [playerId]: status }));
      setSessionPlayerExitReasons(prev => ({ ...prev, [playerId]: exitReason }));
      setLastOperation({ type: 'setStatus', status: 'success', playerId, newStatus: status, exitReason });
      return { success: true, newStatus: status, exitReason };
    } catch (err) {
      const errorMessage = err.message || 'Failed to update player status';
      setError(errorMessage);
      setLastOperation({ type: 'setStatus', status: 'error', playerId, error: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  /**
   * Bulk updates player statuses with new state system
   * @param {string} action - 'activate_all', 'deactivate_active', 'mark_arrived', 'mark_finished'
   * @returns {Promise<Object>} Result object with success status and data/error
   */
  const bulkToggleStatus = useCallback(async (action) => {
    const validActions = ['activate_all', 'deactivate_active', 'mark_arrived', 'mark_finished', 'end_session'];
    if (!validActions.includes(action)) {
      const errorMessage = `Invalid action. Must be one of: ${validActions.join(', ')}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }

    setLoading(true);
    setError(null);
    setLastOperation({ type: 'bulkToggle', status: 'loading', action });

    try {
      if (!sessionId) throw new Error('No session ID');
      const allPlayerIds = Object.keys(playersById);
      const offlineBridge = offlineRosterBridgeRef?.current;
      const applyStatusWithOfflineFallback = async (playerIds, status, exitReason = null) => {
        for (const playerId of playerIds) {
          if (canUseOfflineRosterFallback(offlineBridge)) {
            await offlineBridge.enqueue({
              type: OFFLINE_COMMAND_TYPES.PLAYER_STATUS,
              playerId,
              status,
              exitReason,
            });
            continue;
          }
          try {
            await updateSessionPlayerStatus(sessionId, playerId, status, exitReason);
          } catch (error) {
            if (canUseOfflineRosterFallback(offlineBridge, error)) {
              await offlineBridge.enqueue({
                type: OFFLINE_COMMAND_TYPES.PLAYER_STATUS,
                playerId,
                status,
                exitReason,
              });
            } else {
              throw error;
            }
          }
        }
      };
      
      if (action === 'activate_all') {
        // Activate all signed up players (including paused players)
        // Exclude cancelled and no_show players
        const toActivate = allPlayerIds.filter(id => {
          const status = sessionPlayerStatus[id];
          const exitReason = sessionPlayerExitReasons[id];
          // Skip if no status, already active, cancelled, or no_show
          return status && 
                 status !== 'active' && 
                 status !== 'cancelled' && 
                 !(status === 'inactive' && exitReason === 'no_show');
        });
        await applyStatusWithOfflineFallback(toActivate, 'active');
        setSessionPlayerStatus(status => {
          const copy = { ...status };
          toActivate.forEach(id => { copy[id] = 'active'; });
          return copy;
        });
        setSessionPlayerExitReasons(reasons => {
          const copy = { ...reasons };
          toActivate.forEach(id => { copy[id] = null; });
          return copy;
        });
        
        // Return the activated player IDs
        return { success: true, activatedPlayerIds: toActivate };
      } else if (action === 'deactivate_active') {
        // Deactivate all active players (mark as finished)
        const toDeactivate = allPlayerIds.filter(id => sessionPlayerStatus[id] === 'active');
        for (const playerId of toDeactivate) {
          await updateSessionPlayerStatus(sessionId, playerId, 'inactive', 'finished');
        }
        setSessionPlayerStatus(status => {
          const copy = { ...status };
          toDeactivate.forEach(id => { copy[id] = 'inactive'; });
          return copy;
        });
        setSessionPlayerExitReasons(reasons => {
          const copy = { ...reasons };
          toDeactivate.forEach(id => { copy[id] = 'finished'; });
          return copy;
        });
        
        // Return the deactivated player IDs
        return { success: true, deactivatedPlayerIds: toDeactivate };
      } else if (action === 'mark_arrived') {
        // Mark all signed up players as arrived (active)
        // Only include players with status 'signed_up' (excludes cancelled/no_show)
        const toMarkArrived = allPlayerIds.filter(id => {
          const status = sessionPlayerStatus[id];
          const exitReason = sessionPlayerExitReasons[id];
          // Only mark as arrived if they are signed_up (not cancelled or no_show)
          return status === 'signed_up' && 
                 status !== 'cancelled' && 
                 !(status === 'inactive' && exitReason === 'no_show');
        });
        await applyStatusWithOfflineFallback(toMarkArrived, 'active');
        setSessionPlayerStatus(status => {
          const copy = { ...status };
          toMarkArrived.forEach(id => { copy[id] = 'active'; });
          return copy;
        });
        setSessionPlayerExitReasons(reasons => {
          const copy = { ...reasons };
          toMarkArrived.forEach(id => { copy[id] = null; });
          return copy;
        });
        
        // Return the activated player IDs
        return { success: true, activatedPlayerIds: toMarkArrived };
      } else if (action === 'mark_finished') {
        // Mark all active/paused players as finished
        const toMarkFinished = allPlayerIds.filter(id => 
          sessionPlayerStatus[id] === 'active' || sessionPlayerStatus[id] === 'paused'
        );
        for (const playerId of toMarkFinished) {
          await updateSessionPlayerStatus(sessionId, playerId, 'inactive', 'finished');
        }
        setSessionPlayerStatus(status => {
          const copy = { ...status };
          toMarkFinished.forEach(id => { copy[id] = 'inactive'; });
          return copy;
        });
        setSessionPlayerExitReasons(reasons => {
          const copy = { ...reasons };
          toMarkFinished.forEach(id => { copy[id] = 'finished'; });
          return copy;
        });
        
        // Return the deactivated player IDs
        return { success: true, deactivatedPlayerIds: toMarkFinished };
      } else if (action === 'end_session') {
        // Mark all players as finished while preserving existing exit reasons
        const allSessionPlayers = Object.keys(sessionPlayerStatus);
        
        for (const playerId of allSessionPlayers) {
          const currentStatus = sessionPlayerStatus[playerId];
          const currentExitReason = sessionPlayerExitReasons[playerId];
          
          // Only update players who are not already inactive
          if (currentStatus !== 'inactive') {
            // Preserve existing exit reasons, only set 'finished' if no exit reason exists
            const exitReason = currentExitReason || 'finished';
            await updateSessionPlayerStatus(sessionId, playerId, 'inactive', exitReason);
          }
        }
        
        // Update local state
        setSessionPlayerStatus(status => {
          const copy = { ...status };
          allSessionPlayers.forEach(id => { 
            if (copy[id] !== 'inactive') {
              copy[id] = 'inactive'; 
            }
          });
          return copy;
        });
        
        setSessionPlayerExitReasons(reasons => {
          const copy = { ...reasons };
          allSessionPlayers.forEach(id => {
            if (!copy[id]) {
              copy[id] = 'finished';
            }
          });
          return copy;
        });
      }
      
      setLastOperation({ 
        type: 'bulkToggle', 
        status: 'success', 
        action 
      });

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Failed to bulk update player session status';
      setError(errorMessage);
      setLastOperation({ type: 'bulkToggle', status: 'error', action, error: errorMessage });
      
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  }, [sessionId, clubId, playersById, sessionPlayerStatus]);

  /**
   * Gets session statistics
   * @returns {Object} Statistics object
   */
  const getSessionStats = useCallback(() => {
    const totalPlayers = Object.keys(playersById).length;
    const signedUpCount = Object.values(sessionPlayerStatus).filter(status => status === 'signed_up').length;
    const activePlayersCount = Object.values(sessionPlayerStatus).filter(status => status === 'active').length;
    const pausedCount = Object.values(sessionPlayerStatus).filter(status => status === 'paused').length;
    const inactiveCount = Object.values(sessionPlayerStatus).filter(status => status === 'inactive').length;
    
    return {
      totalPlayers,
      signedUp: signedUpCount,
      active: activePlayersCount,
      paused: pausedCount,
      inactive: inactiveCount,
      activePercentage: totalPlayers > 0 ? Math.round((activePlayersCount / totalPlayers) * 100) : 0
    };
  }, [playersById, sessionPlayerStatus]);

  /**
   * Gets only active players
   * @returns {Array} Array of active players
   */
  const getActivePlayers = useCallback(() => {
    return Object.values(playersById).filter(player => sessionPlayerStatus[player.id] === 'active');
  }, [playersById, sessionPlayerStatus]);

  /**
   * Gets only inactive players
   * @returns {Array} Array of inactive players
   */
  const getInactivePlayers = useCallback(() => {
    return Object.values(playersById).filter(player => sessionPlayerStatus[player.id] !== 'active');
  }, [playersById, sessionPlayerStatus]);

  /**
   * Clears any current error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Clears the last operation state
   */
  const clearLastOperation = useCallback(() => {
    setLastOperation(null);
  }, []);

  // Add a shallowEqual utility above its first usage
  function shallowEqual(objA, objB) {
    if (objA === objB) return true;
    if (!objA || !objB) return false;
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    if (keysA.length !== keysB.length) return false;
    for (let key of keysA) {
      if (objA[key] !== objB[key]) return false;
    }
    return true;
  }

  // Fetch players and session player statuses on mount/club/session change
  // NOTE: fetchPlayers is now called manually from Dashboard.js to ensure proper loading order
  // useEffect(() => {
  //   fetchPlayers();
  // }, [clubId, sessionId]); // Only depend on clubId and sessionId, not the function
  useEffect(() => {
    fetchSessionPlayerStatus();
  }, [sessionId]); // Only depend on sessionId, not the function

  // Always provide a players array derived from playersById
  const players = Object.values(playersById);
  const activePlayers = players.filter(p => sessionPlayerStatus[p.id] === 'active');

  // When returning playersById, use lastGoodPlayersById if playersById is empty but loading
  const effectivePlayersById = (loading && Object.keys(playersById).length === 0)
    ? lastGoodPlayersById.current
    : playersById;

  return {
    players,
    playersById: effectivePlayersById,
    activePlayers,
    sessionPlayerStatus, // { playerId: status }
    setSessionPlayerStatus,
    sessionPlayerExitReasons, // { playerId: exitReason }
    sessionPlayerPenalties, // { playerId: late_joiner_penalty }
    setSessionPlayerPenalties, // setter for sessionPlayerPenalties
    sessionPlayerScheduledGames, // { playerId: scheduled_game_id }
    setSessionPlayerScheduledGames, // setter for sessionPlayerScheduledGames
    loading,
    error,
    lastOperation,
    sessionStats: getSessionStats(),
    togglePlayerStatus,
    bulkToggleStatus,
    setPlayerSessionStatus, // (playerId, status, exitReason)
    clearError,
    clearLastOperation,
    setPlayers: (newPlayers) => {
      // Defensive: accept either array or object
      const arr = Array.isArray(newPlayers) ? newPlayers : Object.values(newPlayers);
      const newMap = {};
      arr.forEach(p => { newMap[p.id] = p; });
      // Debug logging removed for cleaner console
      setPlayersById(newMap);
    },
    fetchPlayers,
    fetchSessionPlayerStatus, // Expose to allow manual refresh of session player status
    patchSessionPlayerFromRow,
  };
};

export default usePlayerManagement; 